import {
    AspectRatio,
    Box,
    FileTile,
    FileTileDeleteButton,
    FileTileThumbnail,
    FlexBox,
    Icon,
    StandardTileImage,
} from '@vp/swan';
import { __ } from '@99designs/i18n';
import type { StagedAttachment } from '../../upload';

export type AttachmentPreviewsProps = {
    attachments: StagedAttachment[];
    /** True while an upload is in flight — renders shimmer skeletons. */
    isUploading: boolean;
    /** Number of in-flight uploads to represent as skeletons. */
    pendingCount: number;
    onRemove: (attachment: StagedAttachment) => void;
};

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * The staged-attachment strip above the composer: one removable tile per uploaded file,
 * plus a shimmer skeleton per in-flight upload. Renders nothing when there is neither.
 */
export function AttachmentPreviews({
    attachments,
    isUploading,
    pendingCount,
    onRemove,
}: AttachmentPreviewsProps) {
    if (attachments.length === 0 && !(isUploading && pendingCount > 0)) {
        return null;
    }

    return (
        // Padded to sit inside the composer's bordered surface; the textarea's own top
        // padding provides the gap to the text below.
        <FlexBox gap={3} pt={4} px={4} style={{ flexWrap: 'wrap' }}>
            {attachments.map((attachment) => (
                <FileTile
                    key={attachment.uploadId}
                    fileName={attachment.fileName}
                    fileDescription={
                        attachment.fileSize
                            ? formatFileSize(Number(attachment.fileSize))
                            : undefined
                    }
                    accessibleTextForProgress={__('Upload progress')}
                    accessibleTextForSpinner={__('Uploading')}
                    style={{ width: '120px' }}
                >
                    <FileTileThumbnail>
                        {attachment.mediaType.startsWith('image/') ? (
                            <img src={attachment.previewUrl} alt={attachment.fileName} />
                        ) : (
                            <Icon iconType="fileGeneric" />
                        )}
                    </FileTileThumbnail>
                    <FileTileDeleteButton
                        accessibleText={__('Remove {{fileName}}', {
                            fileName: attachment.fileName,
                        })}
                        onClick={() => onRemove(attachment)}
                    />
                </FileTile>
            ))}
            {isUploading &&
                Array.from({ length: pendingCount }).map((_, index) => (
                    <Box
                        key={`skeleton-${String(index)}`}
                        aria-busy="true"
                        style={{ width: '120px' }}
                    >
                        <StandardTileImage>
                            <AspectRatio ratio={1} loadingShimmer />
                        </StandardTileImage>
                    </Box>
                ))}
        </FlexBox>
    );
}
